import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import MobileResponsiveTable from "../common/MobileResponsiveTable";
import { useToast } from "../../contexts/ToastContext";
import { AdminContentManager } from "./AdminContentManager";
import { AdminAppointmentManager } from "./AdminAppointmentManager";
import AdminSoftwareWaitlist from "./AdminSoftwareWaitlist";
import { useAuth } from "../../hooks/useSupabaseAuth"
import { API_ENDPOINTS, getAuthHeaders, shouldIncludeCredentials } from '../../lib/config/api-endpoints';
import { useHealerParams } from "../../src/hooks/useUrlParams";
import { ROUTES } from "../../src/constants/routes";

interface Client {
  id: string;
  personalInfo: {
    fullName: string;
    email: string;
    phone: string;
    date: string;
  };
  created_at: string;
  meetingStatus: "upcoming" | "unpaid" | "complete";
  nextMeeting?: {
    timeSlot: string;
    status: string;
  } | null;
  lastMeeting?: {
    timeSlot: string;
    status: string;
  } | null;
  totalBookings: number;
}

interface BookingWithClient {
  id: number;
  client_id: string;
  client_name: string;
  client_email: string;
  product_id: string;
  time_slot: string;
  status: string;
  amount: number;
  currency: string;
  stripe_session_id: string | null;
  payment_intent_id: string | null;
  calendar_event_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface CategorizedBookings {
  upcoming: BookingWithClient[];
  past: BookingWithClient[];
  cancelled: BookingWithClient[];
}

const HealerPortal: React.FC = () => {
  const [email, setEmail] = useState("admin@test.local");
  const [password, setPassword] = useState("admin123");
  const [authMethod, setAuthMethod] = useState<'password' | 'magic-link'>('password');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [bookings, setBookings] = useState<CategorizedBookings>({
    upcoming: [],
    past: [],
    cancelled: [],
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  // Use healer-specific URL parameter management
  const {
    getTab,
    setTab,
    getClientStatus,
    setClientStatus,
    getAppointmentStatus,
    setAppointmentStatus,
    getSearch,
    setSearch,
    getPage,
    setPage,
  } = useHealerParams();
  
  const activeTab = getTab();
  const statusFilter = activeTab === 'clients' ? getClientStatus() : getAppointmentStatus();
  const searchFilter = getSearch();
  
  const { showError, showSuccess } = useToast();
  const { user, isLoading: authLoading, signOut, signInWithPassword, isAdmin } = useAuth();

  useEffect(() => {
    // Check if user is logged in and is admin
    if (!authLoading && user && isAdmin) {
      fetchClientsAndBookings();
    }
  }, [user, authLoading, isAdmin]);
  
  // Tab and filter management now handled by useHealerParams hook
  const setActiveTab = setTab;
  
  // Set filter in URL - now using the hook's methods
  const setFilter = (key: string, value: string) => {
    if (key === 'clientStatus') {
      setClientStatus(value || null);
    } else if (key === 'appointmentStatus') {
      setAppointmentStatus(value || null);
    } else if (key === 'search') {
      setSearch(value || null);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.admin.bookings, {
        headers: getAuthHeaders(API_ENDPOINTS.admin.bookings),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.admin.bookings) ? 'include' : 'same-origin',
      });

      if (response.ok) {
        const bookingsData = await response.json();
        setBookings(bookingsData);
      }
    } catch (err) {
      console.error("Error fetching bookings:", err);
    }
  };

  const fetchClientsAndBookings = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(API_ENDPOINTS.admin.clients, {
        headers: getAuthHeaders(API_ENDPOINTS.admin.clients),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.admin.clients) ? 'include' : 'same-origin',
      });

      if (!response.ok) {
        throw new Error("Failed to fetch clients.");
      }

      const data = await response.json();
      if (data.success) {
        setClients(data.data.clients);
      } else {
        throw new Error(data.error?.message || "Failed to fetch clients");
      }
      setError(null);

      // Also fetch bookings
      await fetchBookings();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSignin = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Use the signInWithPassword from the auth hook
      const result = await signInWithPassword(email, password);
      
      if (result.success) {
        showSuccess("Signed in successfully!");
        // The useAuth hook will handle redirecting once authenticated
      } else {
        setError('Invalid email or password');
        showError('Invalid email or password');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLinkRequest = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Send magic link request using Supabase auth endpoint
      const response = await fetch(API_ENDPOINTS.auth.magicLinkRequest, {
        method: 'POST',
        headers: getAuthHeaders(API_ENDPOINTS.auth.magicLinkRequest),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.auth.magicLinkRequest) ? 'include' : 'same-origin',
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send magic link');
      }

      const data = await response.json();
      
      if (data.success) {
        // Handle local development auto-redirect
        const isLocalDev = import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        
        if (isLocalDev && data.magic_link) {
          console.log('🔧 LOCAL DEV: Auto-redirecting admin to magic link', data.magic_link);
          // Small delay to show the sending state, then redirect
          setTimeout(() => {
            window.location.href = data.magic_link;
          }, 500);
          return;
        }
        
        setMagicLinkSent(true);
        setError(null);
        showSuccess("Magic link sent to your email!");
      } else {
        setError('Failed to send magic link. Please try again.');
        showError('Failed to send magic link. Please try again.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send magic link';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError("Email is required");
      return;
    }

    if (authMethod === 'password') {
      if (!password) {
        setError("Password is required");
        return;
      }
      handlePasswordSignin();
    } else {
      handleMagicLinkRequest();
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-stone-100">
        <div className="text-center">
          <p className="text-brand-green-800">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login form if not authenticated or not admin
  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-stone-100">
        <div className="bg-white shadow-2xl rounded-xl p-8 w-full max-w-md">
          <h1 className="text-3xl font-bold text-center text-brand-green-800 mb-6">
            Healer Portal Login
          </h1>
          
          {!magicLinkSent ? (
            <form onSubmit={handleFormSubmit} className="space-y-6">
              {/* Auth Method Toggle */}
              <div className="flex space-x-1 bg-brand-stone-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setAuthMethod('password')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    authMethod === 'password'
                      ? 'bg-white text-brand-green-700 shadow-sm'
                      : 'hover:text-brand-green-700 text-brand-stone-600'
                  }`}
                >
                  Password
                </button>
                <button
                  type="button"
                  onClick={() => setAuthMethod('magic-link')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    authMethod === 'magic-link'
                      ? 'bg-white text-brand-green-700 shadow-sm'
                      : 'hover:text-brand-green-700 text-brand-stone-600'
                  }`}
                >
                  Magic Link
                </button>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-brand-stone-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-brand-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                  placeholder="Enter healer email address"
                />
              </div>

              {authMethod === 'password' && (
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-brand-stone-700 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-brand-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                    placeholder="Enter password"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-brand-green-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-brand-green-700 transition-colors duration-300 disabled:opacity-50"
              >
                {isLoading 
                  ? (authMethod === 'password' ? 'Signing in...' : 'Sending...') 
                  : (authMethod === 'password' ? 'Sign In' : 'Send Magic Link')
                }
              </button>

              {error && (
                <div className="text-red-600 text-sm text-center">
                  {error}
                </div>
              )}

              <div className="mt-6 text-center text-sm text-brand-stone-500">
                <p>
                  {authMethod === 'password' 
                    ? 'Enter your credentials to access the healer portal'
                    : "We'll send you a secure magic link to access the healer portal"
                  }
                </p>
              </div>
            </form>
          ) : (
            <div className="text-center">
              <div className="text-green-600 text-lg font-semibold mb-4">
                Magic Link Sent!
              </div>
              <div className="text-brand-stone-600 mb-6">
                <p>We've sent a secure link to <strong>{email}</strong></p>
                <p className="mt-2">Check your email and click the link to access the healer portal</p>
              </div>
              <div className="text-sm text-brand-stone-500">
                <p>The link will expire in 1 hour for security</p>
                <p className="mt-2">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button 
                    onClick={() => {
                      setMagicLinkSent(false);
                      setError(null);
                    }}
                    className="text-brand-green-600 hover:text-brand-green-700 underline"
                  >
                    try again
                  </button>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Group clients by meeting status
  const upcomingClients = clients.filter(
    (client) => client.meetingStatus === "upcoming"
  );
  const unpaidClients = clients.filter(
    (client) => client.meetingStatus === "unpaid"
  );
  const completeClients = clients.filter(
    (client) => client.meetingStatus === "complete"
  );

  // Sort clients within each group
  upcomingClients.sort((a, b) => {
    if (a.nextMeeting && b.nextMeeting) {
      return (
        new Date(a.nextMeeting.timeSlot).getTime() -
        new Date(b.nextMeeting.timeSlot).getTime()
      );
    }
    return 0;
  });

  unpaidClients.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  completeClients.sort((a, b) => {
    if (a.lastMeeting && b.lastMeeting) {
      return (
        new Date(b.lastMeeting.timeSlot).getTime() -
        new Date(a.lastMeeting.timeSlot).getTime()
      );
    }
    return 0;
  });

  const formatMeetingTime = (timeSlot: string): string => {
    return new Date(timeSlot).toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatCurrency = (cents: number, currency: string = "USD"): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  };

  const createBookingColumns = (): ColumnDef<BookingWithClient>[] => [
    {
      accessorKey: "client_name",
      header: "Client",
      cell: ({ row }) => {
        const booking = row.original;
        return (
          <Link
            to={`${ROUTES.HEALER.CLIENT_DETAIL.replace(':id', booking.client_id)}?return=${activeTab}`}
            className="font-semibold text-brand-green-700 hover:text-brand-green-800 transition-colors"
          >
            {booking.client_name}
          </Link>
        );
      },
    },
    {
      accessorKey: "time_slot",
      header: "Date & Time",
      cell: ({ row }) => {
        const booking = row.original;
        return (
          <span className="text-sm text-brand-stone-700">
            {formatMeetingTime(booking.time_slot)}
          </span>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const booking = row.original;
        const statusColors: Record<string, string> = {
          confirmed: "bg-green-100 text-green-800",
          completed: "bg-blue-100 text-blue-800",
          cancelled: "bg-red-100 text-red-800",
        };
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[booking.status] || "bg-gray-100 text-gray-800"}`}
          >
            {booking.status}
          </span>
        );
      },
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ row }) => {
        const booking = row.original;
        return (
          <span className="text-sm text-brand-stone-600">
            {formatCurrency(booking.amount, booking.currency)}
          </span>
        );
      },
    },
    {
      accessorKey: "client_email",
      header: "Email",
      cell: ({ row }) => {
        const booking = row.original;
        return (
          <span className="text-sm text-brand-stone-600">
            {booking.client_email}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const booking = row.original;
        return (
          <Link
            to={`${ROUTES.HEALER.CLIENT_DETAIL.replace(':id', booking.client_id)}?return=${activeTab}`}
            className="text-brand-green-600 hover:text-brand-green-700 text-sm font-medium"
          >
            View Client
          </Link>
        );
      },
    },
  ];

  const createClientColumns = (
    statusColor: string,
    statusBg: string
  ): ColumnDef<Client>[] => [
    {
      accessorKey: "personalInfo.fullName",
      header: "Name",
      cell: ({ row }) => {
        const client = row.original;
        const clientName = client.personalInfo?.fullName || "N/A";
        return (
          <Link
            to={`${ROUTES.HEALER.CLIENT_DETAIL.replace(':id', client.id.toString())}?return=${activeTab}&tab=overview`}
            className="font-semibold text-brand-green-700 hover:text-brand-green-800 transition-colors"
          >
            {clientName}
          </Link>
        );
      },
    },
    {
      accessorKey: "meetingStatus",
      header: "Status",
      cell: ({ row }) => {
        const client = row.original;
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${statusBg} ${statusColor}`}
          >
            {client.meetingStatus}
          </span>
        );
      },
    },
    {
      accessorKey: "meetingTime",
      header: "Meeting Time",
      cell: ({ row }) => {
        const client = row.original;
        if (client.meetingStatus === "upcoming" && client.nextMeeting) {
          return (
            <span className="text-sm text-brand-stone-700">
              {formatMeetingTime(client.nextMeeting.timeSlot)}
            </span>
          );
        }
        if (client.meetingStatus === "complete" && client.lastMeeting) {
          return (
            <span className="text-sm text-brand-stone-500">
              {formatMeetingTime(client.lastMeeting.timeSlot)}
            </span>
          );
        }
        if (client.meetingStatus === "unpaid") {
          return (
            <span className="text-sm text-brand-stone-500">
              Submitted: {new Date(client.created_at).toLocaleDateString()}
            </span>
          );
        }
        return <span className="text-sm text-brand-stone-400">-</span>;
      },
    },
    {
      accessorKey: "personalInfo.email",
      header: "Email",
      cell: ({ row }) => {
        const client = row.original;
        return (
          <span className="text-sm text-brand-stone-600">
            {client.personalInfo?.email || "N/A"}
          </span>
        );
      },
    },
    {
      accessorKey: "totalBookings",
      header: "Bookings",
      cell: ({ row }) => {
        const client = row.original;
        return (
          <span className="text-sm text-brand-stone-400">
            {client.totalBookings > 0 ? `${client.totalBookings}` : "0"}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const client = row.original;
        return (
          <Link
            to={`${ROUTES.HEALER.CLIENT_DETAIL.replace(':id', client.id.toString())}?return=${activeTab}&tab=overview`}
            className="text-brand-green-600 hover:text-brand-green-700 text-sm font-medium"
          >
            View Details
          </Link>
        );
      },
    },
  ];

  const renderClientSection = (
    title: string,
    clients: Client[],
    statusColor: string,
    statusBg: string,
    emptyMessage: string
  ) => {
    const columns = createClientColumns(statusColor, statusBg);

    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-brand-green-800">
            {title}
          </h2>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${statusBg} ${statusColor}`}
          >
            {clients.length} {clients.length === 1 ? "client" : "clients"}
          </span>
        </div>

        <MobileResponsiveTable
          data={clients}
          columns={columns}
          searchPlaceholder={`Search ${title.toLowerCase()}...`}
          emptyMessage={emptyMessage}
          pageSize={5}
        />
      </div>
    );
  };

  const renderBookingSection = (
    title: string,
    bookingList: BookingWithClient[],
    statusColor: string,
    statusBg: string,
    emptyMessage: string
  ) => {
    const columns = createBookingColumns();

    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-brand-green-800">
            {title}
          </h2>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${statusBg} ${statusColor}`}
          >
            {bookingList.length}{" "}
            {bookingList.length === 1 ? "appointment" : "appointments"}
          </span>
        </div>

        <MobileResponsiveTable
          data={bookingList}
          columns={columns}
          searchPlaceholder={`Search ${title.toLowerCase()}...`}
          emptyMessage={emptyMessage}
          pageSize={5}
        />
      </div>
    );
  };

  const handleLogout = async () => {
    try {
      // Use the useAuth signOut function which handles the correct endpoint
      await signOut();
      // Navigate to home page after logout
      window.location.href = '/';
    } catch (err) {
      console.error('Logout failed:', err);
      // Navigate anyway if logout fails
      window.location.href = '/';
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-brand-green-800">
            Healer Portal
          </h1>
          {/* Show current URL path for debugging */}
          <p className="text-sm text-brand-stone-500 mt-1">
            Path: {ROUTES.HEALER.DASHBOARD}?{new URLSearchParams({ tab: activeTab }).toString()}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {activeTab === "clients" && (
            <div className="text-sm text-brand-stone-500">
              Total: {clients.length} clients
            </div>
          )}
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-brand-stone-500 text-white rounded-lg hover:bg-brand-stone-600 transition-colors"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-brand-stone-200">
          <nav className="-mb-px flex space-x-8">
            <Link
              to={`${ROUTES.HEALER.DASHBOARD}?tab=clients`}
              onClick={(e) => { e.preventDefault(); setActiveTab("clients"); }}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "clients"
                  ? "border-brand-green-500 text-brand-green-600"
                  : "border-transparent text-brand-stone-500 hover:text-brand-stone-700 hover:border-brand-stone-300"
              }`}
            >
              Client Management
            </Link>
            <Link
              to={`${ROUTES.HEALER.DASHBOARD}?tab=content`}
              onClick={(e) => { e.preventDefault(); setActiveTab("content"); }}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "content"
                  ? "border-brand-green-500 text-brand-green-600"
                  : "border-transparent text-brand-stone-500 hover:text-brand-stone-700 hover:border-brand-stone-300"
              }`}
            >
              Content Management
            </Link>
            <Link
              to={`${ROUTES.HEALER.DASHBOARD}?tab=appointments`}
              onClick={(e) => { e.preventDefault(); setActiveTab("appointments"); }}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "appointments"
                  ? "border-brand-green-500 text-brand-green-600"
                  : "border-transparent text-brand-stone-500 hover:text-brand-stone-700 hover:border-brand-stone-300"
              }`}
            >
              Appointments
            </Link>
            <Link
              to={`${ROUTES.HEALER.DASHBOARD}?tab=appointment-types`}
              onClick={(e) => { e.preventDefault(); setActiveTab("appointment-types"); }}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "appointment-types"
                  ? "border-brand-green-500 text-brand-green-600"
                  : "border-transparent text-brand-stone-500 hover:text-brand-stone-700 hover:border-brand-stone-300"
              }`}
            >
              Appointment Types
            </Link>
            <Link
              to={`${ROUTES.HEALER.DASHBOARD}?tab=software-waitlist`}
              onClick={(e) => { e.preventDefault(); setActiveTab("software-waitlist"); }}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "software-waitlist"
                  ? "border-brand-green-500 text-brand-green-600"
                  : "border-transparent text-brand-stone-500 hover:text-brand-stone-700 hover:border-brand-stone-300"
              }`}
            >
              Software Waitlist
            </Link>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "clients" ? (
        <div>
          {/* Sub-navigation for client status */}
          <div className="mb-6 flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('clientStatus', '')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                !statusFilter
                  ? 'bg-brand-green-600 text-white'
                  : 'bg-brand-stone-100 text-brand-stone-600 hover:bg-brand-stone-200'
              }`}
            >
              All Clients ({clients.length})
            </button>
            <button
              onClick={() => setFilter('clientStatus', 'upcoming')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'upcoming'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              Upcoming ({upcomingClients.length})
            </button>
            <button
              onClick={() => setFilter('clientStatus', 'unpaid')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'unpaid'
                  ? 'bg-orange-600 text-white'
                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
              }`}
            >
              Unpaid ({unpaidClients.length})
            </button>
            <button
              onClick={() => setFilter('clientStatus', 'complete')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'complete'
                  ? 'bg-green-600 text-white'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              Complete ({completeClients.length})
            </button>
          </div>
          {/* Show filtered sections based on clientStatus param */}
          {(!statusFilter || statusFilter === 'upcoming') && renderClientSection(
            "Upcoming Meetings",
            upcomingClients,
            "text-blue-700",
            "bg-blue-100",
            "No upcoming meetings scheduled"
          )}

          {(!statusFilter || statusFilter === 'unpaid') && renderClientSection(
            "Unpaid Submissions",
            unpaidClients,
            "text-orange-700",
            "bg-orange-100",
            "All clients have booked their appointments"
          )}

          {(!statusFilter || statusFilter === 'complete') && renderClientSection(
            "Completed Meetings",
            completeClients,
            "text-green-700",
            "bg-green-100",
            "No completed meetings yet"
          )}
        </div>
      ) : activeTab === "content" ? (
        <AdminContentManager />
      ) : activeTab === "appointments" ? (
        <div>
          {/* Sub-navigation for appointment status */}
          <div className="mb-6 flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('appointmentStatus', '')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                !statusFilter
                  ? 'bg-brand-green-600 text-white'
                  : 'bg-brand-stone-100 text-brand-stone-600 hover:bg-brand-stone-200'
              }`}
            >
              All Appointments ({bookings.upcoming.length + bookings.past.length + bookings.cancelled.length})
            </button>
            <button
              onClick={() => setFilter('appointmentStatus', 'upcoming')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'upcoming'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              Upcoming ({bookings.upcoming.length})
            </button>
            <button
              onClick={() => setFilter('appointmentStatus', 'past')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'past'
                  ? 'bg-gray-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Past ({bookings.past.length})
            </button>
            <button
              onClick={() => setFilter('appointmentStatus', 'cancelled')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'cancelled'
                  ? 'bg-red-600 text-white'
                  : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
            >
              Cancelled ({bookings.cancelled.length})
            </button>
          </div>
          {/* Show filtered sections based on appointmentStatus param */}
          {(!statusFilter || statusFilter === 'upcoming') && renderBookingSection(
            "Upcoming Appointments",
            bookings.upcoming,
            "text-blue-700",
            "bg-blue-100",
            "No upcoming appointments scheduled"
          )}

          {(!statusFilter || statusFilter === 'past') && renderBookingSection(
            "Past Appointments",
            bookings.past,
            "text-gray-700",
            "bg-gray-100",
            "No past appointments found"
          )}

          {(!statusFilter || statusFilter === 'cancelled') && renderBookingSection(
            "Cancelled Appointments",
            bookings.cancelled,
            "text-red-700",
            "bg-red-100",
            "No cancelled appointments"
          )}
        </div>
      ) : activeTab === "appointment-types" ? (
        <AdminAppointmentManager />
      ) : (
        <AdminSoftwareWaitlist />
      )}
    </div>
  );
};

export default HealerPortal;
